/**
 * Structural email checks shared across user, facility, and admin forms.
 * Complements simple regex checks: length limits, single @, safe local/domain shape, sensible TLD.
 * Call with a **trimmed** string; empty string returns false.
 */

function isValidLocalPart(local: string): boolean {
  if (!local || local.length > 64) {
    return false;
  }
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
    return false;
  }
  // Unquoted local-part subset (letters, digits, common specials; apostrophe for names)
  return /^[-a-zA-Z0-9._'%+]+$/.test(local);
}

function isValidDomainLabel(label: string): boolean {
  if (!label || label.length > 63) {
    return false;
  }
  return /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/.test(label);
}

function isValidTld(tld: string): boolean {
  if (tld.length < 2 || /^\d+$/.test(tld)) {
    return false;
  }
  return /^[a-z]{2,}$/i.test(tld) || /^xn--[a-z0-9-]+$/i.test(tld);
}

function isValidDomain(domain: string): boolean {
  if (!domain || domain.length > 253) {
    return false;
  }
  if (
    domain.startsWith('.') ||
    domain.endsWith('.') ||
    domain.includes('..') ||
    !domain.includes('.')
  ) {
    return false;
  }
  const labels = domain.split('.');
  if (!labels.every(isValidDomainLabel)) {
    return false;
  }
  const tld = labels.at(-1);
  return tld ? isValidTld(tld) : false;
}

export function isWellFormedEmailAddress(trimmed: string): boolean {
  if (!trimmed || /\s/.test(trimmed)) {
    return false;
  }
  if (trimmed.length > 254) {
    return false;
  }
  const atParts = trimmed.split('@');
  if (atParts.length !== 2) {
    return false;
  }
  const [local, domain] = atParts;
  return isValidLocalPart(local) && isValidDomain(domain);
}
